if (!customElements.get('cardori-knot-set-configurator-v2')) {
  class CardoriKnotSetConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.initializationStarted) return;
      this.initializationStarted = true;

      Promise.all([
        customElements.whenDefined('variant-picker'),
        customElements.whenDefined('custom-select')
      ]).then(() => this.initialize());
    }

    initialize() {
      if (this.initialized) return;

      this.section = this.closest('.shopify-section');
      this.productForm = this.section ? this.section.querySelector('product-form') : null;
      this.submitForm = this.productForm ? this.productForm.querySelector('.js-product-form') : null;
      this.variantData = this.querySelector('[data-cardori-knot-variants]');
      this.controls = Array.from(this.querySelectorAll('select'));

      if (!this.productForm || !this.submitForm || !this.section || !this.variantData) return;

      try {
        this.variants = JSON.parse(this.variantData.textContent);
      } catch (error) {
        return;
      }

      if (!this.variants.length || !this.getNativePicker()) return;

      this.initialized = true;
      this.controls.forEach((control) => {
        control.addEventListener('change', () => this.selectVariant());
      });
      this.productForm.addEventListener('click', (event) => {
        const submitButton = event.target.closest('button[type="submit"], [name="add"]');
        if (!submitButton || !this.productForm.contains(submitButton)) return;

        const selectedVariant = this.getSelectedVariant();
        if (selectedVariant) this.syncVariantId(selectedVariant.id);
      }, { capture: true });
      this.productForm.addEventListener('submit', () => {
        const selectedVariant = this.getSelectedVariant();
        if (selectedVariant) this.syncVariantId(selectedVariant.id);
      }, { capture: true });
      this.section.addEventListener('on:variant:change', (event) => {
        if (!event.detail || !event.detail.variant) return;
        this.syncControls(event.detail.variant.options);
        this.syncVariantId(event.detail.variant.id);
      });

      this.productForm.classList.add('cardori-knot-set-ready');

      const initialVariant = this.getSelectedVariant();
      if (initialVariant) this.syncVariantId(initialVariant.id);
    }

    getNativePicker() {
      const picker = this.productForm.querySelector('variant-picker');
      if (!picker) return null;

      const selectors = picker.querySelectorAll('.option-selector');
      return selectors.length === 3 ? picker : null;
    }

    getSelections() {
      const upperColour = this.querySelector('[data-cardori-colour="upper"]').value;
      const lowerColour = this.querySelector('[data-cardori-colour="lower"]').value;
      const upperLength = this.querySelector('[data-cardori-length="upper"]').value;
      const lowerLength = this.querySelector('[data-cardori-length="lower"]').value;

      return [
        `${upperColour} oben · ${lowerColour} unten`,
        upperLength,
        lowerLength
      ];
    }

    getSelectedVariant() {
      const options = this.getSelections();
      return this.variants.find((item) => item.options.every((value, index) => value === options[index]));
    }

    selectVariant() {
      const options = this.getSelections();
      const variant = this.getSelectedVariant();
      const picker = this.getNativePicker();

      if (!variant || !picker) return;

      let eventTarget = null;
      options.forEach((value, index) => {
        const selector = picker.querySelector(`.option-selector[data-index="${index + 1}"]`);
        if (!selector) return;

        const desiredOption = Array.from(selector.querySelectorAll('.custom-select__option'))
          .find((option) => option.dataset.value === value);
        const customSelect = desiredOption ? desiredOption.closest('custom-select') : null;
        if (!desiredOption || !customSelect) return;

        const selectedOption = customSelect.querySelector('.custom-select__option[aria-selected="true"]');
        if (selectedOption && selectedOption !== desiredOption) {
          selectedOption.setAttribute('aria-selected', 'false');
        }
        desiredOption.setAttribute('aria-selected', 'true');
        customSelect.selectedOption = desiredOption;
        if (customSelect.nativeSelect) customSelect.nativeSelect.value = desiredOption.dataset.value;
        customSelect.button.firstElementChild.textContent = desiredOption.firstElementChild.textContent;
        customSelect.listbox.setAttribute('aria-activedescendant', desiredOption.id);
        eventTarget = customSelect;
      });

      if (!eventTarget) return;

      this.syncVariantId(variant.id);
      eventTarget.dispatchEvent(new CustomEvent('change', {
        bubbles: true,
        cancelable: false,
        detail: {
          selectedValue: options[2],
          variantId: String(variant.id),
          noFocus: true
        }
      }));
    }

    syncVariantId(variantId) {
      const idInput = this.productForm.querySelector('input[name="id"]');
      if (!idInput || !variantId) return;

      idInput.value = String(variantId);
      idInput.setAttribute('value', String(variantId));
      this.syncSetDetails(variantId);
    }


    syncSetDetails(variantId) {
      const variant = this.variants.find((item) => String(item.id) === String(variantId));
      const form = this.submitForm || this.productForm.querySelector('.js-product-form');
      if (!variant || !form || !Array.isArray(variant.options) || variant.options.length !== 3) return;
      const colours = variant.options[0].match(/^(.+) oben · (.+) unten$/);
      if (!colours) return;
      [[1, 'Unteres Armband'], [0, 'Oberes Armband']].forEach(([index, label]) => {
        const name = 'properties[' + label + ']';
        let field = Array.from(form.elements).find((input) => input.name === name);
        if (!field) {
          field = document.createElement('input');
          field.type = 'hidden';
          field.name = name;
          form.prepend(field);
        }
        let length = variant.options[index + 1];
        if (!length.includes('Verlängerung')) length += ' Verlängerung';
        const value = colours[index + 1] + ' · ' + length;
        if (field.value !== value) {
          field.value = value;
          field.setAttribute('value', value);
        }
      });
    }

    syncControls(options) {
      if (!Array.isArray(options) || options.length !== 3) return;

      const colourParts = options[0].split(' oben · ');
      const lowerColour = (colourParts[1] || '').replace(/ unten$/, '');
      const values = [colourParts[0], lowerColour, options[1], options[2]];
      const selectors = [
        '[data-cardori-colour="upper"]',
        '[data-cardori-colour="lower"]',
        '[data-cardori-length="upper"]',
        '[data-cardori-length="lower"]'
      ];

      selectors.forEach((selector, index) => {
        const control = this.querySelector(selector);
        if (control && values[index]) control.value = values[index];
      });
    }
  }

  customElements.define('cardori-knot-set-configurator-v2', CardoriKnotSetConfigurator);
}

